import React, { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Grid,
  AppBar,
  Toolbar,
  Alert,
  CircularProgress,
  Step,
  StepLabel,
  Stepper,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { styled, useMediaQuery } from '@mui/system';
import { useAuth } from '../contexts/AuthContext';
import { teacherService } from '../services/teacher.service';
import LogoutButton from '../components/LogoutButton';
import { getActivePublicRegions } from '../services/regions.services';
import { theme } from '../theme/theme';

interface Region {
  id: string;
  name: string;
  location: string;
  state: string;
  schools: string[];
}

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, 'Phone number must be 10 digits')
    .required('Phone number is required'),
  address: Yup.string().required('Address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  zipCode: Yup.string()
    .matches(/^[0-9]{5}$/, 'Zip code must be 5 digits')
    .required('Zip code is required'),
  regionId: Yup.string().required('Region is required'),
  school: Yup.string().required('School is required'),
  customSchool: Yup.string().when('school', {
    is: 'other',
    then: (schema) => schema.required('Please enter your school name'),
    otherwise: (schema) => schema,
  }),
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(6),
  },
}));

const StyledForm = styled(Form)({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
});

const TeacherRegistration: React.FC = () => {
  const { authStatus, completeRegistration } = useAuth();
  const [registrationError, setRegistrationError] = useState<string | null>(
    null
  );
  const [classCode, setClassCode] = useState<string>('');
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const steps = ['Personal Info', 'Region Info'];
  const [activeStep, setActiveStep] = useState(0);
  const [regions, setRegions] = useState<Region[]>([]);

  // Add this state for the selected region's schools
  const [availableSchools, setAvailableSchools] = useState<string[]>([]);

  const generateClassCode = async () => {
    try {
      setIsGeneratingCode(true);
      const code = await teacherService.generateUniqueClassCode();
      setClassCode(code);
    } catch (error) {
      console.error('Error generating class code:', error);
      setRegistrationError('Failed to generate class code');
    } finally {
      setIsGeneratingCode(false);
    }
  };

  React.useEffect(() => {
    generateClassCode();
    fetchRegions();
  }, []);

  // Fetch regions from the database that are active
  const fetchRegions = async () => {
    try {
      const rawData = await getActivePublicRegions();

      const data: Region[] = rawData.map((region: any) => ({
        id: region.id,
        name: region.name,
        location: region.location,
        state: region.state,
        schools: region.schools || [], // Ensure schools is at least an empty array
      }));
      setRegions(data);
    } catch (err) {
      console.error('Error fetching regions:', err);
      setRegistrationError('Failed to load regions. Please try again later.');
    }
  };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      setRegistrationError(null);

      if (!authStatus.user) {
        throw new Error('User not authenticated');
      }

      if (!classCode) {
        throw new Error('Class code generation failed');
      }

      const registrationData = {
        ...values,
        // If "other" is selected, use the custom school name
        school: values.school === 'other' ? values.customSchool : values.school,
        userType: 'teacher',
        classCode,
        status: 'active',
      };

      // Remove the customSchool field before submitting
      delete registrationData.customSchool;

      // Register the teacher
      await completeRegistration(registrationData);

      // Create the initial class document
      await teacherService.initializeClass(authStatus.user.uid, classCode);
    } catch (error) {
      console.error('Registration error:', error);
      setRegistrationError(
        'An error occurred during registration. Please try again.'
      );
      setSubmitting(false);
    }
  };

  return (
    <>
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            Teacher Registration
          </Typography>
          <LogoutButton />
        </Toolbar>
      </AppBar>
      <Box sx={{ maxWidth: 600, margin: 'auto', mt: 4, px: 2 }}>
        <StyledPaper elevation={3}>
          <Typography variant="h4" gutterBottom align="center" color="primary">
            Complete Your Registration
          </Typography>

          {registrationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {registrationError}
            </Alert>
          )}

          <Stepper
            activeStep={activeStep}
            alternativeLabel={!isMobile}
            orientation={isMobile ? 'vertical' : 'horizontal'}
            sx={{ mb: 4 }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Your Class Code
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: 'grey.100',
                p: 2,
                borderRadius: 1,
              }}
            >
              {isGeneratingCode ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <Typography variant="h5" fontWeight="bold">
                    {classCode}
                  </Typography>
                  <Button
                    size="small"
                    onClick={generateClassCode}
                    disabled={isGeneratingCode}
                  >
                    Generate New Code
                  </Button>
                </>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Share this code with your students to let them join your class
            </Typography>
          </Box>

          <Formik
            initialValues={{
              firstName: '',
              lastName: '',
              phone: '',
              address: '',
              city: '',
              state: '',
              zipCode: '',
              regionId: '',
              school: '',
              customSchool: '',
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              touched,
              isSubmitting,
              values,
              setFieldValue,
              setTouched,
            }) => (
              <StyledForm>
                {activeStep === 0 && (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="firstName"
                          label="First Name"
                          error={touched.firstName && Boolean(errors.firstName)}
                          helperText={touched.firstName && errors.firstName}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="lastName"
                          label="Last Name"
                          error={touched.lastName && Boolean(errors.lastName)}
                          helperText={touched.lastName && errors.lastName}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="phone"
                          label="Phone Number"
                          error={touched.phone && Boolean(errors.phone)}
                          helperText={touched.phone && errors.phone}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="address"
                          label="Address"
                          error={touched.address && Boolean(errors.address)}
                          helperText={touched.address && errors.address}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="city"
                          label="City"
                          error={touched.city && Boolean(errors.city)}
                          helperText={touched.city && errors.city}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="state"
                          label="State"
                          error={touched.state && Boolean(errors.state)}
                          helperText={touched.state && errors.state}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="zipCode"
                          label="Zip Code"
                          error={touched.zipCode && Boolean(errors.zipCode)}
                          helperText={touched.zipCode && errors.zipCode}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {activeStep === 1 && (
                  <>
                    <FormControl
                      fullWidth
                      error={touched.regionId && Boolean(errors.regionId)}
                    >
                      <InputLabel id="region-select-label">Region</InputLabel>
                      <Field
                        as={Select}
                        labelId="region-select-label"
                        id="region-select"
                        name="regionId"
                        label="Region"
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          const regionId = e.target.value;
                          setFieldValue('regionId', regionId);

                          // Reset school selection when region changes
                          setFieldValue('school', '');
                          setFieldValue('customSchool', '');

                          // Find the selected region and update available schools
                          const selectedRegion = regions.find(
                            (r) => r.id === regionId
                          );
                          if (selectedRegion) {
                            setAvailableSchools(selectedRegion.schools || []);
                          } else {
                            setAvailableSchools([]);
                          }
                        }}
                      >
                        {regions.length > 0 ? (
                          regions.map((region) => (
                            <MenuItem key={region.id} value={region.id}>
                              {region.name} ({region.location}, {region.state})
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled value="">
                            Loading regions...
                          </MenuItem>
                        )}
                      </Field>
                      {touched.regionId && errors.regionId && (
                        <Typography
                          color="error"
                          variant="caption"
                          sx={{ ml: 2, mt: 0.5 }}
                        >
                          {errors.regionId}
                        </Typography>
                      )}
                    </FormControl>

                    {/* School dropdown - only show if a region is selected */}
                    {values.regionId && (
                      <FormControl
                        fullWidth
                        error={touched.school && Boolean(errors.school)}
                        sx={{ mt: 2 }}
                      >
                        <InputLabel id="school-select-label">School</InputLabel>
                        <Field
                          as={Select}
                          labelId="school-select-label"
                          id="school-select"
                          name="school"
                          label="School"
                        >
                          {/* Add schools if available */}
                          {availableSchools.length > 0 ? (
                            availableSchools.map((school) => (
                              <MenuItem key={school} value={school}>
                                {school}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem
                              disabled
                              value=""
                              sx={{ color: 'text.secondary' }}
                            >
                              No schools defined in this region
                            </MenuItem>
                          )}
                          <MenuItem value="other">Other (Specify)</MenuItem>
                        </Field>
                        {touched.school && errors.school && (
                          <Typography
                            color="error"
                            variant="caption"
                            sx={{ ml: 2, mt: 0.5 }}
                          >
                            {errors.school}
                          </Typography>
                        )}
                      </FormControl>
                    )}

                    {/* Custom School input field - only show if "Other" is selected */}
                    {values.school === 'other' && (
                      <Field
                        as={TextField}
                        fullWidth
                        name="customSchool"
                        label="Enter School Name"
                        margin="normal"
                        error={
                          touched.customSchool && Boolean(errors.customSchool)
                        }
                        helperText={touched.customSchool && errors.customSchool}
                      />
                    )}
                  </>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep((prev) => prev - 1)}
                  >
                    Back
                  </Button>
                  {activeStep === steps.length - 1 ? (
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Complete Registration'}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      onClick={async () => {
                        const allErrors: Record<string, string> =
                          await validationSchema
                            .validate(values, { abortEarly: false })
                            .then(() => ({}))
                            .catch((err) => {
                              const formatted: Record<string, string> = {};
                              err.inner.forEach((e: any) => {
                                if (e.path) formatted[e.path] = e.message;
                              });
                              return formatted;
                            });

                        const fieldsToCheck =
                          activeStep === 0
                            ? [
                                'firstName',
                                'lastName',
                                'phone',
                                'address',
                                'city',
                                'state',
                                'zipCode',
                              ]
                            : [
                                'regionId',
                                'school',
                                ...(values.school === 'other'
                                  ? ['customSchool']
                                  : []),
                              ];

                        const touchedFields = fieldsToCheck.reduce(
                          (acc, field) => {
                            acc[field] = true;
                            return acc;
                          },
                          {} as Record<string, boolean>
                        );

                        // Use Formik's `setTouched` properly
                        await setTouched(touchedFields, true);

                        const hasStepErrors = fieldsToCheck.some((field) =>
                          Boolean(allErrors[field])
                        );

                        if (!hasStepErrors) {
                          setActiveStep((prev) => prev + 1);
                        }
                      }}
                    >
                      Next
                    </Button>
                  )}
                </Box>
              </StyledForm>
            )}
          </Formik>
        </StyledPaper>
      </Box>
    </>
  );
};

export default TeacherRegistration;
